import { UseFormSetValue } from 'react-hook-form'

import { PaymentTypeDetails } from 'models/PaymentTypeDetails'
import { Products } from 'models/Plans'
import moment from 'moment'
import { parseCookies } from 'nookies'
import { ChargeRecurrenceBodyModalForm } from 'templates/Charge/interface'
import { v4 as uuidv4 } from 'uuid'

import { InternalIdentifier } from 'components/IdentifyInternal/IdentifyInternal.interface'
import { enumNature } from 'components/Modal/ModalInvoice/ModalInvoice.utils'

import { formatCents } from 'utils'
import { currency, priceConverter } from 'utils/FormatMoney'
import {
  FREQUENCY_ENUM_TO_NAME,
  FREQUENCY_NAME_TO_ENUM,
  monthsPerCycle,
  presetToPair
} from 'utils/frequency'

export function getLimitDays() {
  const { ['nextauth.limit_days_for_payment']: limit_days_for_payment } =
    parseCookies()
  return limit_days_for_payment || '60'
}

// SC-11742: prazo padrão de negativação do merchant (régua de cobrança). Só
// preenche o campo "Negativar"; cada cobrança pode mudar.
export function getDefaultDaysToReport() {
  const { ['nextauth.default_days_to_report']: default_days_to_report } =
    parseCookies()
  return default_days_to_report || '30'
}

function getIssueDays() {
  const { ['nextauth.days_to_issue_billing']: days_to_issue_billing } =
    parseCookies()
  return days_to_issue_billing || '0'
}

function getIssueBillingType(): 'days_before_due_date' | 'specific_day' {
  const { ['nextauth.issue_billing_type']: issue_billing_type } = parseCookies()
  return (
    (issue_billing_type as 'days_before_due_date' | 'specific_day') ||
    'days_before_due_date'
  )
}

/**
 * SC-10999: prazos padrão de uma empresa a partir da sessão dela (o retorno do
 * `useSessionCookies`). Os de `sharedInitialFormValues` saem dos cookies da
 * empresa de contexto na carga do módulo; num formulário com empresa escolhida
 * na visão multi-empresas valem os desta.
 */
export const companyDeadlineDefaults = (cookies: Record<string, string>) => {
  const issueDays = cookies['nextauth.days_to_issue_billing'] || '0'
  const issueBillingType =
    (cookies['nextauth.issue_billing_type'] as
      | 'days_before_due_date'
      | 'specific_day') || 'days_before_due_date'

  return {
    expirationField: cookies['nextauth.limit_days_for_payment'] || '60',
    daysToReport: cookies['nextauth.default_days_to_report'] || '30',
    issueDays,
    hasIssueDays: Number(issueDays) > 0,
    issueBillingType,
    specificDayOfMonth:
      issueBillingType === 'specific_day' ? Number(issueDays) || 1 : 1
  }
}

// Valores iniciais compartilhados para formulários de cobrança/recorrência
export const sharedInitialFormValues = {
  discountDays: new Date(),
  discountPolicy: false,
  dueDate: new Date(),
  expirationField: getLimitDays(),
  interestPolicy: false,
  // Sem default: a frequência da recorrência é DERIVADA dos planos escolhidos (o item de
  // menor período dita). Deixar "Mensal" pré-marcado fazia o merchant herdar uma escolha
  // que nunca fez — e que passa a ser consequência da composição.
  // Produto e link de pagamento, que não derivam de composição, mantêm o default próprio.
  frequency: undefined as number | undefined,
  payments: [] as string[], // Nenhuma opção de pagamento ativa por padrão
  payment_type_details: {
    // Recebimento manual (previsão) — ver PaymentsTypeValues.RecebimentoManual.
    direct_payment: { enabled: false },
    pix: { enabled: false, max_installments: 1, fixed_installments: false },
    boleto: { enabled: false, max_installments: 1, fixed_installments: false },
    boleto_pix: { enabled: false, max_installments: 1 },
    credit_card: {
      enabled: false,
      automatic_renew: false,
      max_installments: 1,
      max_installments_without_fee: null,
      fixed_installments: false,
      pass_card_fees: false,
      credit_card: { enabled: false },
      credit_card_installments: { enabled: false },
      credit_card_recurring: { enabled: false }
    }
  },
  isEnablePlan: false,
  interestFee: '1.0',
  interestFine: '2.0',
  interestNegativation: false,
  daysToReport: getDefaultDaysToReport(),
  totalCycles: 0,

  iss_retention: 'no',
  issue_when: 'on_billing_issued',
  nature_operation: '1',
  amount_type: 'amount_issued',
  due_date_period: 'on_before_due_date' as any,
  // New fields for specific day configuration
  issue_mode: 'relative_days' as 'relative_days' | 'specific_day',
  specific_day: 1,
  specific_month_offset: '0' as '-2' | '-1' | '0' | '1' | '2',
  service_list_code: '',
  nfse_issuer_uuid: '',
  descriptionInvoice: '',
  copy_description: false,
  nfse_installment_scope: 'per_cycle',
  billingDescription: '',
  hasIdentifyInternal: false,
  internalIdentifiers: [],
  hasBonus: false,
  hasIssueDays: Number(getIssueDays()) > 0,
  issueDays: getIssueDays(),
  issueBillingType: getIssueBillingType(),
  specificDayOfMonth:
    getIssueBillingType() === 'specific_day' ? Number(getIssueDays()) || 1 : 1,
  discounts: [],
  bonusSettings: {
    cyclesCount: 1,
    cyclesCountCustom: '',
    type: 'value' as 'value' | 'percentage',
    amount: '',
    startCycle: 1
  },
  items: [
    {
      price: undefined,
      quantity: 1,
      product: undefined,
      description: '',
      typedValue: '' as const,
      enable_customer_select_quantity: false,
      minimum_quantity: 1
    }
  ],
  hasItemsOnDescription: false,
  description: '',
  chargeType: 1,
  dueType: 'fixed_date',
  proratedCharge: false,
  // Adjustment settings
  hasAdjustment: false,
  adjustmentSettings: {
    adjustment_type: '' as '' | 'customized' | 'ipca' | 'igpm',
    frequency: 'annual' as 'quarterly' | 'semester' | 'annual',
    percentage_value: '',
    next_adjustment_date: '',
    automatic_application: true,
    billing_item_uuids: null as string[] | null
  }
}

export interface FormatProps {
  dataForm: ChargeRecurrenceBodyModalForm
  isUnique: boolean
  isCharge: boolean
  isEdit: boolean
  editType?: 'charge' | 'recurrence'
  planDefault?: Products
  isProductOrLink?: boolean
  isLink?: boolean
}

/**
 * Escopo de itens do reajuste, no formato que o backend espera em cada contexto.
 *
 * Edição: os itens já existem e a seleção viaja como `billing_item_uuids`.
 * Criação: os itens ainda não têm uuid de servidor — `UuidIdentifiable#assign_uuid`
 * é um `before_create` que sobrescreve e o payload do item é montado com
 * `.except(:uuid)` —, então a seleção viaja como POSIÇÃO em `billing_items_attributes`
 * e o backend resolve para uuids depois do save.
 *
 * Sem seleção manda `billing_item_uuids: null`, que o backend lê como "todos os itens".
 */
export function buildAdjustmentItemScope(
  adjustmentSettings: ChargeRecurrenceBodyModalForm['adjustmentSettings'],
  isEdit: boolean,
  totalItems: number
) {
  const selected = adjustmentSettings?.billing_item_uuids

  // Nada desmarcado => null. "Todos marcados" e "todos os itens" parecem iguais na
  // tela, mas guardar a lista congelaria o escopo: item adicionado depois ficaria
  // fora do reajuste sem o merchant ter pedido isso.
  if (!selected?.length || selected.length === totalItems) {
    return { billing_item_uuids: null }
  }

  if (isEdit) {
    // Item ainda não salvo não pode entrar no escopo: ele só ganha uuid depois do save e
    // `PATCH /v3/recurrences/:uuid` aceita APENAS billing_item_uuids (o
    // billing_item_indexes da criação não existe nos strong params da edição). O rótulo
    // posicional `item-N` do accordion não casa com registro nenhum e o backend responde
    // 400 "Item(ns) não pertencente(s) a esta recorrência". O accordion já impede a
    // seleção desses itens; aqui é só a rede de segurança para o payload.
    return {
      billing_item_uuids: selected
        .filter(Boolean)
        .filter((value) => !/^item-\d+$/.test(value))
    }
  }

  return {
    billing_item_indexes: selected
      .map((position) => Number(position))
      .filter((position) => Number.isInteger(position) && position >= 0)
  }
}

// Função utilitária para calcular o valor total dos itens
export function calculateTotalValue(items: any[]) {
  const total = items.reduce((sum, item) => {
    // Se o item já tem total calculado, usar ele; senão calcular
    if (item.total !== undefined && item.total !== null) {
      return sum + Number(item.total)
    }
    const price = priceConverter(item.price || 0)
    const quantity = Number(item.quantity) || 0
    return sum + price * quantity
  }, 0)
  // Arredondar para 2 casas decimais para evitar erros de ponto flutuante
  return Math.round(total * 100) / 100
}

// Interface para os dados compartilhados que podem vir de product, charge ou recurrence
export interface SharedFormData {
  // Dados de desconto
  discounts?: Array<{
    amount: string | number
    days: number | string
    policy?: string
    uuid?: string
  }>
  discountPolicy?: string | boolean
  hasIssueDays?: boolean
  issueDays?: string | number
  issue_days?: string | number // snake_case version from API
  issueBillingType?: 'days_before_due_date' | 'specific_day'
  issue_billing_type?: 'days_before_due_date' | 'specific_day' // snake_case version from API
  specificDayOfMonth?: number
  // Dados de juros
  interestPolicy?: string | boolean
  interestFee?: string
  interestFine?: string
  interestNegativation?: boolean
  daysToReport?: string | number

  // Dados de periodicidade
  frequency?: number | string
  frequency_type?: 'days' | 'weeks' | 'months' | 'years'
  frequency_number?: number
  totalCycles?: number
  dueDay?: number | string

  // Dados de vencimento/expiração
  expirationField?: string | number
  limitDaysForPayment?: string | number

  // Dados de pagamento
  paymentType?: string | string[]
  paymentTypeDetails?: PaymentTypeDetails

  // Dados de nota fiscal
  nfseScheduled?: any
  nfsesScheduled?: any // Para charges/recurrences
  nfsePolicy?: string
  nfseIssuerUuid?: string
  // New fields for specific day NF configuration
  issue_mode?:
    | 'relative_days'
    | 'specific_day'
    | 'before_due_date'
    | 'after_due_date'
  configuration_type?: string | number // Campo da API que mapeia para issue_mode
  specific_day?: number
  day_of_month?: number // Campo da API para dia específico
  specific_month_offset?: '-2' | '-1' | '0' | '1' | '2'
  due_date_relation?: string // Campo da API para relação com vencimento
  days?: number | string // Dias antes/depois do vencimento

  // Dados de bonificação
  hasBonus?: boolean
  bonusSettings?: {
    type: 'value' | 'percentage'
    amount: string | number
    cyclesCount: number
    startCycle?: number
  }

  // Dados de identificação interna
  metadata?: Record<string, any>

  // Dados de negativação
  negativation?: {
    days_to_report: number | null
  }

  // Items da cobrança/recorrência
  items?: Array<{
    uuid?: string
    description?: string
    amount_unit?: number
    product?: {
      name?: string
      uuid?: string
      total_cycles?: number
    }
    quantity?: number
    amount_billed?: number
    enable_customer_select_quantity?: boolean
    minimum_quantity?: number
  }>

  // Descrição da cobrança para produtos/links
  billingDescription?: string

  // Campos específicos de produtos/links (TypesCharges)
  chargeType?: string | number
  dueType?: string
  proratedCharge?: boolean

  // Tags
  tags?: string | string[] | any[]

  // Adjustment scheduled data
  adjustmentScheduled?: {
    uuid: string
    referenceable_type: string
    first_adjustment_date: string
    next_adjustment_date: string
    frequency: 'quarterly' | 'semester' | 'annual'
    adjustment_type: 'customized' | 'ipca' | 'igpm'
    percentage_value: string | null
    automatic_application: boolean
    billing_item_uuids?: string[] | null
    applies_to_all_items?: boolean
    created_at: string
    updated_at: string
  }
}

// Função utilitária para setar dados compartilhados no formulário
export const setSharedFormData = (
  data: SharedFormData,
  setValue: UseFormSetValue<any>,
  replaceDiscounts?: (discounts: any[]) => void,
  isRecurrence?: boolean,
  setIdentifyInternal?: React.Dispatch<React.SetStateAction<any[]>>,
  isProductOrLink?: boolean
) => {
  // Processar descontos - seguindo a lógica original que funcionava
  if (data.discounts && Array.isArray(data.discounts)) {
    // Para FormProducts que usa replaceDiscounts
    if (replaceDiscounts) {
      const discounts = data.discounts.map((discount) => {
        let amountToSave

        // Lógica original: policy === 'percentage' para percentual
        if (discount.policy === 'percentage') {
          amountToSave = discount.amount
        } else {
          // Para valor fixo, formatamos para exibição em reais
          amountToSave = formatCents(discount.amount?.toString()) || '0'
        }

        // Handle total_cycles: if > 12, use "Outros" (13) with custom value
        // Support both camelCase (from constructorDiscounts) and snake_case (raw API)
        const apiTotalCycles =
          (discount as any).totalCycles ?? (discount as any).total_cycles ?? 0
        const totalCycles = apiTotalCycles > 12 ? 13 : apiTotalCycles
        const totalCyclesCustom =
          apiTotalCycles > 12 ? apiTotalCycles.toString() : ''

        return {
          amount: amountToSave.toString(),
          days: discount.days.toString(),
          uuid: discount.uuid || uuidv4(),
          type: discount.policy === 'percentage' ? '0' : '1',
          totalCycles,
          totalCyclesCustom,
          startCycle:
            (discount as any).startCycle ?? (discount as any).start_cycle ?? 1
        }
      })

      replaceDiscounts(discounts)

      // Set global discount cycle fields from first discount
      if (discounts.length > 0) {
        const first = discounts[0]
        setValue('discountTotalCycles', first.totalCycles ?? 0)
        setValue('discountTotalCyclesCustom', first.totalCyclesCustom ?? '')
        setValue('discountStartCycle', first.startCycle ?? 1)
      }
    } else {
      // Para BodyModalNewChargeOrRecurrence que usa setValue diretamente
      const discounts = data.discounts.map((discount) => {
        let amountToSave

        if (discount.policy === 'percentage') {
          amountToSave = discount.amount
        } else {
          amountToSave = formatCents(discount.amount?.toString()) || '0'
        }

        // Handle total_cycles: if > 12, use "Outros" (13) with custom value
        // Support both camelCase (from constructorDiscounts) and snake_case (raw API)
        const apiTotalCycles =
          (discount as any).totalCycles ?? (discount as any).total_cycles ?? 0
        const totalCycles = apiTotalCycles > 12 ? 13 : apiTotalCycles
        const totalCyclesCustom =
          apiTotalCycles > 12 ? apiTotalCycles.toString() : ''

        const discountData: any = {
          amount: amountToSave.toString(),
          type: discount.policy === 'percentage' ? '0' : '1',
          totalCycles,
          totalCyclesCustom,
          startCycle:
            (discount as any).startCycle ?? (discount as any).start_cycle ?? 1
        }

        // Para produtos/links e recorrências, usar days diretamente
        // Para cobranças normais, calcular a data baseada nos dias
        if (isRecurrence || isProductOrLink) {
          discountData.days = discount.days.toString()
        } else {
          // Para charges normais, calcular a data baseada nos dias usando a dueDate da cobrança
          // Usar moment para evitar problemas de timezone com new Date() em strings ISO
          const chargeDueDate = (data as any).dueDate
            ? moment((data as any).dueDate)
                .subtract(Number(discount.days), 'days')
                .toDate()
            : new Date()
          discountData.date = chargeDueDate
        }

        return discountData
      })

      setValue('discounts', discounts)

      // Set global discount cycle fields from first discount
      if (discounts.length > 0) {
        const first = discounts[0]
        setValue('discountTotalCycles', first.totalCycles ?? 0)
        setValue('discountTotalCyclesCustom', first.totalCyclesCustom ?? '')
        setValue('discountStartCycle', first.startCycle ?? 1)
      }
    }
  }

  // Setar política de desconto
  if (data.discountPolicy !== undefined) {
    const hasDiscounts = data.discounts && data.discounts.length > 0
    setValue(
      'discountPolicy',
      data.discountPolicy === 'no_discount' ? false : hasDiscounts
    )
  }

  // Setar dados de juros
  if (data.interestPolicy !== undefined) {
    const interestBool =
      typeof data.interestPolicy === 'boolean'
        ? data.interestPolicy
        : data.interestPolicy !== 'no_interest'
    setValue('interestPolicy', interestBool)
  }
  if (data.interestFee) {
    setValue('interestFee', data.interestFee)
  }
  if (data.interestFine) {
    setValue('interestFine', data.interestFine)
  }

  // Setar dados de negativação
  if (data.negativation !== undefined) {
    setValue('interestNegativation', data.negativation.days_to_report !== null)
    setValue(
      'daysToReport',
      data.negativation.days_to_report?.toString() || getDefaultDaysToReport()
    )
  } else if (data.interestNegativation !== undefined) {
    setValue('interestNegativation', data.interestNegativation)
    if (data.daysToReport !== undefined) {
      setValue('daysToReport', data.daysToReport.toString())
    }
  }

  // Setar dados de periodicidade
  if (data.frequency !== undefined) {
    const frequencyEnum =
      typeof data.frequency === 'string'
        ? FREQUENCY_NAME_TO_ENUM[data.frequency] ?? 1
        : Number(data.frequency)
    setValue('frequency', frequencyEnum)

    // O par canônico acompanha sempre a frequência: nos presets ele é derivado
    // (atalho de UI), na personalizada vem do que o merchant configurou.
    const preset = FREQUENCY_ENUM_TO_NAME[frequencyEnum] ?? 'monthly'
    const [unit, count] = presetToPair(
      preset,
      data.frequency_type,
      data.frequency_number
    )
    setValue('frequency_type', unit)
    setValue('frequency_number', count)
  }
  if (data.totalCycles !== undefined) {
    if (data.totalCycles > 12) {
      setValue('totalCycles', 13)
      setValue('totalCyclesCustom', data.totalCycles.toString())
    } else {
      setValue('totalCycles', data.totalCycles)
      setValue('totalCyclesCustom', '')
    }
  }
  // Setar dueDay - mas não para produtos/links, pois eles têm tratamento específico depois
  if (data.dueDay !== undefined && data.dueDay !== null && !isProductOrLink) {
    // Para recorrências, dueDay é um select de dias (1-31) - sempre número
    // Para charges, pode ser uma data ou string
    if (isRecurrence) {
      // Converter string para número para o select
      const dayNumber =
        typeof data.dueDay === 'string'
          ? parseInt(data.dueDay, 10)
          : Number(data.dueDay)
      setValue('dueDay', dayNumber)
    } else {
      setValue('dueDay', data.dueDay?.toString())
    }
  }

  // Setar dados de expiração. Ambos os campos são anuláveis no backend
  // (limit_days_for_payment): sem limite próprio, cai no padrão do merchant.
  if (data.expirationField != null) {
    setValue('expirationField', data.expirationField.toString())
  }
  if (data.limitDaysForPayment != null) {
    setValue('expirationField', data.limitDaysForPayment.toString())
  }

  // Setar dados de pagamento a partir de payment_type_details (novo formato)
  if (data.paymentTypeDetails) {
    const adjustedPaymentTypeDetails = { ...data.paymentTypeDetails }
    if (adjustedPaymentTypeDetails.credit_card) {
      const cc = adjustedPaymentTypeDetails.credit_card
      const maxInst = cc.max_installments
      const maxInstWithoutFee = cc.max_installments_without_fee

      // Se os valores forem iguais, setar null (switch desligado)
      if (
        maxInst !== null &&
        maxInst !== undefined &&
        maxInstWithoutFee !== null &&
        maxInstWithoutFee !== undefined &&
        maxInst === maxInstWithoutFee
      ) {
        adjustedPaymentTypeDetails.credit_card = {
          ...cc,
          max_installments_without_fee: null
        }
      }
    }

    setValue('payment_type_details', adjustedPaymentTypeDetails as any)

    const paymentsArray: string[] = []
    // Recebimento manual (previsão) é excludente: quando ativo, é o único meio.
    if (data.paymentTypeDetails.direct_payment?.enabled)
      paymentsArray.push('direct_payment')
    if (data.paymentTypeDetails.pix?.enabled) paymentsArray.push('pix')
    if (data.paymentTypeDetails.boleto?.enabled) paymentsArray.push('boleto')
    // Boleto Pix (se existir no contexto de UI)
    if (data.paymentTypeDetails.boleto_pix?.enabled)
      paymentsArray.push('boleto_pix')

    const cc = data.paymentTypeDetails.credit_card
    if (cc) {
      const anyCC =
        cc.credit_card?.enabled ||
        cc.credit_card_installments?.enabled ||
        cc.credit_card_recurring?.enabled
      if (anyCC) {
        // Marca a opção visual de Cartão de Crédito na UI
        paymentsArray.push('credit_card')
      }
    }

    setValue('payments', Array.from(new Set(paymentsArray)))
  }

  // Setar dados de nota fiscal - nova lógica baseada na política NFSe
  // Verificar se tem nfsesScheduled (charges/recurrences) ou nfseScheduled (products)
  const nfseData = (data as any).nfsesScheduled || data.nfseScheduled

  // Nova lógica: verificar se nfsePolicy existe e é diferente de 'no_nfse'
  if (data.nfsePolicy && data.nfsePolicy !== 'no_nfse') {
    setValue('interestInvoice', true)

    // Verificar se é política customizada (on_before_due_date, on_after_due_date ou custom_schedule)
    const isCustomPolicy =
      data.nfsePolicy === 'on_before_due_date' ||
      data.nfsePolicy === 'on_after_due_date' ||
      data.nfsePolicy === 'custom_schedule'

    setValue(
      'issue_when',
      isCustomPolicy ? 'on_custom_issued' : data.nfsePolicy
    )

    // Configurar due_date_period e days para políticas customizadas
    if (isCustomPolicy) {
      // Para custom_schedule, não setar due_date_period
      if (data.nfsePolicy !== 'custom_schedule') {
        setValue(
          'due_date_period',
          data.nfsePolicy as 'on_before_due_date' | 'on_after_due_date'
        )
      }

      // Handle configuration type - pode vir como issue_mode ou configuration_type
      const configType =
        nfseData?.issue_mode ||
        data.issue_mode ||
        nfseData?.configuration_type ||
        data.configuration_type

      if (configType) {
        // Mapear configuration_type para issue_mode se necessário
        let issueModeValue = configType

        if (configType === 'relative_days') {
          // Para relative_days, usar o nfsePolicy para determinar se é before ou after
          if (data.nfsePolicy === 'on_before_due_date') {
            issueModeValue = 'before_due_date'
          } else if (data.nfsePolicy === 'on_after_due_date') {
            issueModeValue = 'after_due_date'
          } else {
            // Fallback para before_due_date
            issueModeValue = 'before_due_date'
          }
        } else if (
          configType === 'specific_day_of_month' ||
          configType === 1 ||
          configType === 'specific_day'
        ) {
          issueModeValue = 'specific_day'
        } else if (configType === 'before_due_date') {
          issueModeValue = 'before_due_date'
        } else if (configType === 'after_due_date') {
          issueModeValue = 'after_due_date'
        }

        setValue('issue_mode', issueModeValue)

        if (
          issueModeValue === 'specific_day' ||
          configType === 1 ||
          configType === 'specific_day_of_month'
        ) {
          // Configuração de dia específico do mês
          setValue(
            'specific_day',
            nfseData?.day_of_month || data.day_of_month || 1
          )

          // Mapear due_date_relation (string) para offset '-2'..'2' (UI)
          const relation = nfseData?.due_date_relation || data.due_date_relation

          const offset: '-2' | '-1' | '0' | '1' | '2' =
            relation === 'two_months_before'
              ? '-2'
              : relation === 'month_before'
              ? '-1'
              : relation === 'same_month'
              ? '0'
              : relation === 'month_after'
              ? '1'
              : relation === 'two_months_after'
              ? '2'
              : '0'
          setValue('specific_month_offset', offset)
        } else if (
          issueModeValue === 'before_due_date' ||
          issueModeValue === 'after_due_date'
        ) {
          // Configuração de dias relativos (antes/depois do vencimento)
          // Setar o campo 'days_offset' que é usado pelo componente Invoice
          if (nfseData?.days !== undefined && nfseData?.days !== null) {
            setValue('days_offset', nfseData.days.toString())
          } else if (data.days !== undefined && data.days !== null) {
            setValue('days_offset', data.days.toString())
          }
        }
      } else {
        // Fallback: tentar inferir do nfsePolicy
        if (data.nfsePolicy === 'custom_schedule') {
          setValue('issue_mode', 'specific_day')
          if (nfseData?.day_of_month) {
            setValue('specific_day', nfseData.day_of_month)
          }
          if (nfseData?.due_date_relation) {
            const relation = nfseData.due_date_relation as string
            const offset: '-2' | '-1' | '0' | '1' | '2' =
              relation === 'two_months_before'
                ? '-2'
                : relation === 'month_before'
                ? '-1'
                : relation === 'same_month'
                ? '0'
                : relation === 'month_after'
                ? '1'
                : relation === 'two_months_after'
                ? '2'
                : '0'
            setValue('specific_month_offset', offset)
          }
        } else if (
          data.nfsePolicy === 'on_before_due_date' ||
          data.nfsePolicy === 'on_after_due_date'
        ) {
          // Para políticas before/after sem configuration_type, inferir o issue_mode
          setValue(
            'issue_mode',
            data.nfsePolicy === 'on_before_due_date'
              ? 'before_due_date'
              : 'after_due_date'
          )
          // days pode vir null quando o agendamento da cobrança ficou
          // dessincronizado da política (ex.: on_billing_paid sem dias)
          if (nfseData?.days !== undefined && nfseData?.days !== null) {
            setValue('days_offset', nfseData.days.toString())
          }
        }
      }
    }

    // Configurar amountCustom se amount_type for 'amount_custom'
    if (nfseData?.amount_type === 'amount_custom' && nfseData?.amount) {
      setValue('amountCustom', formatCents(nfseData.amount.toString()))
    }

    // Configurar outros campos de NFSe se nfseData existir
    if (nfseData) {
      setValue('iss_retention', nfseData.iss_retention ? 'yes' : 'no')
      setValue('service_list_code', nfseData.nfse_issuer_service?.uuid || '')
      setValue('descriptionInvoice', nfseData.description || '')
      setValue('copy_description', nfseData.copy_description || false)
      setValue(
        'nfse_installment_scope',
        nfseData.nfse_installment_scope || 'per_cycle'
      )

      // Para recorrências, usar 'amount_paid' como padrão, para charges usar 'amount_issued'
      setValue(
        'amount_type',
        nfseData.amount_type || (isRecurrence ? 'amount_paid' : 'amount_issued')
      )

      if (nfseData.nature_operation) {
        setValue(
          'nature_operation',
          enumNature[nfseData.nature_operation as keyof typeof enumNature] ||
            '1'
        )
      }

      if (nfseData.nfse_issuer?.uuid) {
        setValue('nfse_issuer_uuid', nfseData.nfse_issuer.uuid)
      }
    }
  } else {
    setValue('interestInvoice', false)
  }

  // Setar UUID do emissor se fornecido diretamente
  if (data.nfseIssuerUuid) {
    setValue('nfse_issuer_uuid', data.nfseIssuerUuid)
  }

  // Setar dados de bonificação
  if (data.hasBonus !== undefined) {
    setValue('hasBonus', data.hasBonus)

    if (data.hasBonus && data.bonusSettings) {
      setValue('bonusSettings.type', data.bonusSettings.type || 'value')

      // Formatar o valor da bonificação de acordo com o tipo
      if (data.bonusSettings.type === 'value') {
        setValue(
          'bonusSettings.amount',
          formatCents(data.bonusSettings.amount?.toString() || '')
        )
      } else {
        setValue(
          'bonusSettings.amount',
          data.bonusSettings.amount?.toString() || ''
        )
      }

      // Configurar a quantidade de ciclos
      if (data.bonusSettings.cyclesCount > 12) {
        setValue('bonusSettings.cyclesCount', 13)
        setValue(
          'bonusSettings.cyclesCountCustom',
          data.bonusSettings.cyclesCount.toString()
        )
      } else {
        setValue(
          'bonusSettings.cyclesCount',
          data.bonusSettings.cyclesCount || 1
        )
        setValue('bonusSettings.cyclesCountCustom', '')
      }

      // Configurar o ciclo inicial
      setValue('bonusSettings.startCycle', data.bonusSettings.startCycle || 1)
    }
  }

  // Setar campos específicos de produtos/links (TypesCharges)
  if (isProductOrLink) {
    if (data.chargeType !== undefined) {
      // chargeType pode vir como string ('single', 'recurrent') ou número (0, 1)
      const chargeTypeValue =
        typeof data.chargeType === 'string'
          ? data.chargeType === 'single'
            ? 0
            : 1
          : data.chargeType
      setValue('chargeType', chargeTypeValue)
    }
    if (data.dueType !== undefined) {
      setValue('dueType', data.dueType)
    }
    if (data.proratedCharge !== undefined) {
      setValue('proratedCharge', data.proratedCharge)
    }
    // Setar dueDay para produtos/links - pode vir como número ou string
    if (data.dueDay !== undefined && data.dueDay !== null) {
      const dueDayValue =
        typeof data.dueDay === 'string'
          ? parseInt(data.dueDay, 10)
          : Number(data.dueDay)
      setValue('dueDay', dueDayValue)
    }
  }

  // Setar dados de identificação interna
  if (data.metadata && Object.keys(data.metadata).length > 0) {
    setValue('hasIdentifyInternal', true)

    const internalIdentifiers: any[] = []
    Object.keys(data.metadata).forEach((key) => {
      if (key && data.metadata?.[key] !== undefined) {
        internalIdentifiers.push({
          name: key,
          value: data.metadata[key],
          uuid: uuidv4()
        })
      }
    })

    // Sempre tentar setar no campo internalIdentifiers do formulário
    try {
      setValue('internalIdentifiers', internalIdentifiers)
    } catch (error) {
      // Se falhar, ignorar silenciosamente
    }

    // Para ModalNewPaymentLink que também usa setIdentifyInternal (compatibilidade)
    if (setIdentifyInternal) {
      setIdentifyInternal(internalIdentifiers)
    }
  } else {
    setValue('hasIdentifyInternal', false)
    try {
      setValue('internalIdentifiers', [])
    } catch (error) {
      // Se falhar, ignorar silenciosamente
    }
    if (setIdentifyInternal) {
      setIdentifyInternal([])
    }
  }

  // Setar items da cobrança/recorrência
  if (data.items && Array.isArray(data.items)) {
    const mappedItems = data.items.map((item) => ({
      uuid: item.uuid,
      description: item.description || undefined,
      price: Number(item.amount_unit) || 0,
      product: item.product
        ? {
            label: item.product?.name,
            value: item.product?.uuid,
            ...(item.product?.total_cycles && {
              total_cycles: item.product.total_cycles
            }),
            // O par canônico precisa sobreviver ao mapeamento: a trava de
            // compatibilidade de cadência e o cálculo de cycle_interval leem daqui.
            // Sem ele, o efeito do ItemRow reescreve o passo do item para 1 ao abrir
            // a edição — um item semestral seria salvo como todo ciclo.
            frequency: (item.product as any)?.frequency,
            frequency_type: (item.product as any)?.frequency_type,
            frequency_number: (item.product as any)?.frequency_number
          }
        : undefined,
      quantity: Number(item.quantity) || 1,
      total: Number(item.amount_billed) || 0,
      typedValue: '',
      // Incluir enable_customer_select_quantity e minimum_quantity para payment links
      enable_customer_select_quantity:
        (item as any).enable_customer_select_quantity || false,
      minimum_quantity: (item as any).minimum_quantity ?? 1,
      // Opções avançadas por item (tags + janela de ciclo)
      tags: ((item as any).tags || []).map((tag: any) => ({
        id: tag.uuid || tag.id,
        name: tag.name,
        color: tag.color
          ? tag.color.startsWith('#')
            ? tag.color
            : `#${tag.color}`
          : undefined
      })),
      start_cycle: (item as any).start_cycle ?? 1,
      total_cycles: (item as any).total_cycles ?? 0,
      cycle_interval: (item as any).cycle_interval ?? 1,
      prorate: (item as any).prorate ?? true,
      bonus_policy: (item as any).bonus_policy || 'no_bonus',
      bonus_amount: Number((item as any).bonus_amount) || 0,
      bonus_start_cycle: (item as any).bonus_start_cycle ?? 1,
      bonus_total_cycles: (item as any).bonus_total_cycles ?? 0
    }))
    setValue('items', mappedItems)
  }

  // Setar tags - deixar para o useEffect no SharedChargeFields processar
  // pois precisa das availableTags para converter corretamente
  if (data.tags) {
    // Apenas setar o valor bruto das tags, o useEffect no SharedChargeFields
    // irá processar com prepareFormDataWithTags quando availableTags estiver disponível
    setValue('tags', data.tags)
  }
  // Support both camelCase and snake_case field names from different sources
  const issueDaysValue = data.issueDays ?? data.issue_days
  const issueBillingTypeValue = data.issueBillingType ?? data.issue_billing_type

  if (data.hasIssueDays !== undefined) {
    setValue('hasIssueDays', data.hasIssueDays)
    if (issueBillingTypeValue) {
      setValue('issueBillingType', issueBillingTypeValue)
      // Set the correct field based on issue billing type
      if (issueBillingTypeValue === 'specific_day') {
        setValue('specificDayOfMonth', Number(issueDaysValue) || 1)
      } else {
        setValue('issueDays', Number(issueDaysValue) || 0)
      }
    } else {
      setValue('issueDays', Number(issueDaysValue) || 0)
    }
  } else if (
    issueDaysValue !== undefined &&
    issueDaysValue !== null &&
    Number(issueDaysValue) > 0
  ) {
    setValue('hasIssueDays', true)
    if (issueBillingTypeValue) {
      setValue('issueBillingType', issueBillingTypeValue)
      // Set the correct field based on issue billing type
      if (issueBillingTypeValue === 'specific_day') {
        setValue('specificDayOfMonth', Number(issueDaysValue) || 1)
      } else {
        setValue('issueDays', Number(issueDaysValue))
      }
    } else {
      setValue('issueDays', Number(issueDaysValue))
    }
  } else if (issueDaysValue !== undefined) {
    setValue('hasIssueDays', false)
    setValue('issueDays', Number(issueDaysValue) || 0)
  }

  // Setar dados de reajuste agendado (adjustment scheduled)
  if (data.adjustmentScheduled) {
    setValue('hasAdjustment', true)
    setValue('adjustmentSettings', {
      adjustment_type: data.adjustmentScheduled.adjustment_type,
      frequency: data.adjustmentScheduled.frequency,
      percentage_value: data.adjustmentScheduled.percentage_value || '',
      next_adjustment_date: data.adjustmentScheduled.next_adjustment_date,
      automatic_application: data.adjustmentScheduled.automatic_application,
      // null/vazio no backend significa "todos os itens" — a tabela do accordion
      // materializa isso marcando tudo, sem precisar de um campo de escopo.
      billing_item_uuids: data.adjustmentScheduled.billing_item_uuids ?? null
    })
  }
}

// Builder compartilhado do sub-objeto `nfse_scheduled_attributes`. Extraído de
// `formatSharedChargeData` sem mudança funcional, para reuso no modal de
// configuração de NF em massa.
export const buildNfseScheduledAttributes = (
  dataForm: ChargeRecurrenceBodyModalForm,
  { isLink = false }: { isLink?: boolean } = {}
) => ({
  // Handle both legacy and new configuration modes
  // Só enviar campos customizados se issue_when for 'on_custom_issued'
  ...(dataForm.issue_when === 'on_custom_issued' &&
  dataForm?.issue_mode === 'specific_day'
    ? {
        issue_mode: 'specific_day',
        // Legado mantido
        specific_day: dataForm.specific_day,
        specific_month_offset: dataForm.specific_month_offset,
        // Novos campos para dia específico do mês
        configuration_type: 'specific_day_of_month',
        day_of_month: String(dataForm.specific_day),
        due_date_relation:
          (dataForm as any).specific_month_offset === '-2'
            ? 'two_months_before'
            : (dataForm as any).specific_month_offset === '-1'
            ? 'month_before'
            : (dataForm as any).specific_month_offset === '0'
            ? 'same_month'
            : (dataForm as any).specific_month_offset === '1'
            ? 'month_after'
            : (dataForm as any).specific_month_offset === '2'
            ? 'two_months_after'
            : 'same_month'
      }
    : dataForm.issue_when === 'on_custom_issued'
    ? {
        issue_mode: 'relative_days',
        configuration_type: 'relative_days',
        ...(((dataForm as any)?.days !== undefined ||
          (dataForm as any)?.days_offset !== undefined) &&
          String((dataForm as any)?.days ?? (dataForm as any)?.days_offset) !==
            '' && {
            days: Number(
              (dataForm as any).days ?? (dataForm as any).days_offset
            )
          })
      }
    : {}),
  nfse_policy: !dataForm?.interestInvoice
    ? 'no_nfse'
    : dataForm.issue_when === 'on_custom_issued' &&
      (dataForm as any)?.issue_mode === 'specific_day'
    ? 'custom_schedule'
    : dataForm.issue_when === 'on_custom_issued'
    ? dataForm.due_date_period ||
      ((dataForm as any).issue_mode === 'before_due_date'
        ? 'on_before_due_date'
        : (dataForm as any).issue_mode === 'after_due_date'
        ? 'on_after_due_date'
        : 'on_custom_issued')
    : dataForm?.issue_when,
  amount_type: isLink
    ? // Para links, converter string para número usando o enum do backend
      dataForm?.amount_type === 'amount_paid'
      ? 1
      : dataForm?.amount_type === 'amount_issued'
      ? 2
      : dataForm?.amount_type === 'amount_custom'
      ? 3
      : 2 // default para amount_issued
    : dataForm?.amount_type, // Para outros casos, manter como string
  ...(dataForm?.amount_type === 'amount_custom' &&
    dataForm?.amountCustom && {
      amount: currency(dataForm?.amountCustom)
        .replace(/\./g, '')
        .replace(',', '.')
    }),
  description: dataForm?.descriptionInvoice,
  iss_retention: dataForm?.iss_retention === 'yes' ? true : false,
  nfse_issuer_uuid: dataForm?.nfse_issuer_uuid,
  nfse_issuer_service_uuid: dataForm?.service_list_code,
  nature_operation: Number(dataForm?.nature_operation),
  copy_description: dataForm?.copy_description,
  nfse_installment_scope: dataForm?.nfse_installment_scope || 'per_cycle'
})

export const formatSharedChargeData = (props: FormatProps) => {
  const {
    dataForm,
    isUnique,
    isCharge,
    isEdit,
    editType,
    planDefault,
    isProductOrLink = false,
    isLink = false
  } = props

  // Só a edição de recorrência envia o uuid dos itens. Fatura única (Billing#change)
  // e produto/link recriam os itens do zero no backend, então o uuid não tem efeito
  // útil lá e manter o payload inalterado evita risco nesses fluxos. Coberto por
  // SC-10362 em sharedCharge.utils.test.ts.
  const includeItemUuid = isEdit && !isUnique && !isLink && !isProductOrLink

  // SC-11768: os updates de cobrança, recorrência, plano e link de pagamento só mexem
  // em tags (e, na recorrência, no reajuste) quando a chave vem no corpo. A edição manda
  // o estado inteiro, então limpar as tags ou desligar o reajuste precisa ir explícito
  // ('' / null), em vez de omitir a chave como antes.
  const isRecurrenceEdit = isEdit && editType === 'recurrence'

  // Common data structure for both create and edit
  const data = {
    ...(dataForm.credit_card_uuid && {
      credit_card_uuid: dataForm?.credit_card_uuid?.value
    }),
    ...(dataForm.plan &&
      dataForm.plan !== 'initial' &&
      dataForm.isEnablePlan &&
      !isCharge && {
        product_uuid: dataForm.plan
      }),
    ...(planDefault &&
      dataForm.isEnablePlan &&
      !isCharge && {
        product_uuid: planDefault.uuid
      }),
    ...(dataForm?.hasIssueDays && {
      // Use specificDayOfMonth when issueBillingType is 'specific_day', otherwise use issueDays
      issue_days:
        dataForm?.issueBillingType === 'specific_day'
          ? dataForm?.specificDayOfMonth || 1
          : dataForm?.issueDays,
      issue_billing_type: dataForm?.issueBillingType || 'days_before_due_date'
    }),

    description: dataForm.description,
    // Para produtos/links, adicionar billing_description separadamente
    ...(isProductOrLink && {
      billing_description: dataForm.billingDescription
    }),
    discounts_attributes: [] as {
      amount: number
      days: number
      policy: number
    }[],
    // Para produtos e links, sempre usar limit_days_for_payment
    // Para charges/recorrências normais, usar a lógica original
    ...(isProductOrLink
      ? {
          limit_days_for_payment: parseInt(dataForm.expirationField as string)
        }
      : isUnique
      ? {
          expiration_date: moment(dataForm.dueDate)
            .add(
              dataForm.expirationField === dataForm.dueDate
                ? 1
                : dataForm.expirationField,
              'day'
            )
            .format('YYYY-MM-DD')
        }
      : {
          limit_days_for_payment: parseInt(dataForm.expirationField as string)
        }),
    metadata: {},
    ...(isCharge || !isEdit
      ? {
          due_date: moment(dataForm.dueDate).format('YYYY-MM-DD')
        }
      : {}),
    ...((!isCharge || editType === 'recurrence') && {
      due_day: dataForm.dueDay || moment(dataForm.dueDate).date()
    }),
    // TypesCharges specific fields for products/links
    ...(isProductOrLink && {
      charge_type: dataForm.chargeType === 0 ? 'single' : 'recurrent',
      ...(dataForm.dueType && {
        due_type: dataForm.dueType
      }),
      ...(dataForm.dueDay && {
        due_day: Number(dataForm.dueDay)
      }),
      ...(dataForm.proratedCharge !== undefined && {
        prorated_charge: dataForm.proratedCharge
      })
    }),
    discount_policy:
      dataForm.discountPolicy &&
      dataForm.discounts &&
      dataForm.discounts.length > 0
        ? 4
        : 1,
    interest_policy: dataForm.interestPolicy ? 3 : 1,
    ...(dataForm.interestPolicy && {
      interest_fee: dataForm.interestFee,
      interest_fine: dataForm.interestFine
    }),
    // Add bonus_attributes with the new format
    bonus_policy: !dataForm.hasBonus
      ? 1
      : dataForm.bonusSettings?.type === 'value'
      ? 2
      : 3,
    ...(dataForm.hasBonus && {
      bonus_attributes: {
        policy: !dataForm.hasBonus
          ? 1
          : dataForm.bonusSettings?.type === 'value'
          ? 2
          : 3,
        total_cycles:
          dataForm.bonusSettings?.cyclesCount === 13
            ? Number(dataForm.bonusSettings?.cyclesCountCustom)
            : dataForm.bonusSettings?.cyclesCount,
        amount:
          dataForm.bonusSettings?.type === 'value'
            ? dataForm.bonusSettings?.amount
              ? Number(
                  currency(dataForm.bonusSettings.amount)
                    .replace(/\./g, '')
                    .replace(',', '.')
                )
              : 0
            : Number(dataForm.bonusSettings?.amount || 0),
        start_cycle: dataForm.bonusSettings?.startCycle || 1
      }
    }),

    // Adjustment scheduled attributes
    ...(isRecurrenceEdit &&
      !(dataForm.hasAdjustment && dataForm.adjustmentSettings) && {
        adjustment_scheduled_attributes: null
      }),
    ...(dataForm.hasAdjustment &&
      dataForm.adjustmentSettings && {
        adjustment_scheduled_attributes: {
          adjustment_type: dataForm.adjustmentSettings.adjustment_type,
          frequency: dataForm.adjustmentSettings.frequency,
          percentage_value:
            dataForm.adjustmentSettings.adjustment_type === 'customized'
              ? Number(
                  dataForm.adjustmentSettings.percentage_value
                    ?.replace(',', '.')
                    ?.replace('%', '') || 0
                )
              : null,
          next_adjustment_date:
            dataForm.adjustmentSettings.next_adjustment_date,
          automatic_application:
            dataForm.adjustmentSettings.automatic_application,
          // Escopo do reajuste. Na EDIÇÃO os itens já têm uuid de servidor e a
          // seleção viaja como billing_item_uuids. Na CRIAÇÃO ainda não têm — o
          // UuidIdentifiable sobrescreve o uuid no before_create e o payload do
          // item é montado com .except(:uuid) — então o backend recebe as
          // POSIÇÕES em billing_items_attributes e resolve depois do save.
          ...buildAdjustmentItemScope(
            dataForm.adjustmentSettings,
            isEdit,
            dataForm.items?.length || 0
          )
        }
      }),

    payments: [] as any[],
    ...(dataForm?.interestNegativation
      ? isLink
        ? {
            billing_no_payment_report_scheduled_attributes: {
              days_to_report: Number(dataForm?.daysToReport)
            }
          }
        : {
            negativation: {
              days_to_report: Number(dataForm?.daysToReport)
            }
          }
      : isLink
      ? { billing_no_payment_report_scheduled_attributes: null }
      : { negativation: null }),
    total_cycles: isUnique
      ? 1
      : dataForm.totalCycles === 13
      ? dataForm.totalCyclesCustom
      : dataForm.totalCycles,
    frequency: dataForm.frequency,
    // O par canônico vai sempre junto. Para presets o backend o re-deriva (é
    // idempotente); para a personalizada ele é a única fonte do intervalo.
    ...(() => {
      const preset = FREQUENCY_ENUM_TO_NAME[Number(dataForm.frequency)]
      const [unit, count] = presetToPair(
        preset ?? 'monthly',
        dataForm.frequency_type,
        Number(dataForm.frequency_number) || undefined
      )
      return { frequency_type: unit, frequency_number: count }
    })(),
    nfse_policy: !dataForm?.interestInvoice
      ? 'no_nfse'
      : dataForm.issue_when === 'on_custom_issued' &&
        (dataForm as any)?.issue_mode === 'specific_day'
      ? 'custom_schedule'
      : dataForm.issue_when === 'on_custom_issued'
      ? dataForm.due_date_period ||
        ((dataForm as any).issue_mode === 'before_due_date'
          ? 'on_before_due_date'
          : (dataForm as any).issue_mode === 'after_due_date'
          ? 'on_after_due_date'
          : 'on_custom_issued')
      : dataForm?.issue_when,
    ...(dataForm?.interestInvoice && {
      nfse_scheduled_attributes: buildNfseScheduledAttributes(dataForm, {
        isLink
      })
    }),
    billing_items_attributes: dataForm.items.map((item) => {
      const quantity = Number(item.quantity) || 0

      const formatPrice = (value: number) => {
        const string = value.toString()
        const length = string.length
        const integerPart = length > 2 ? string.slice(0, length - 2) : '0'
        const decimalPart = string.slice(-2)
        // Arredondar para 2 casas decimais para evitar erros de ponto flutuante
        return (
          Math.round((Number(integerPart) + Number(decimalPart) / 100) * 100) /
          100
        )
      }

      // Identidade do item na edição de recorrência. Sem o uuid, o backend cai no casamento
      // por CONTEÚDO (amount_unit + quantity + description + product_id) e, ao editar o valor
      // de um item, destrói e recria o registro — perdendo tags e bonificação e empurrando o
      // start_cycle para o piso current_cycle + 1. Foi esse mesmo mecanismo que disparou o bug
      // do loop de diff do Stateful. Item novo vem com uuid vazio e aí o backend precisa
      // tratá-lo como novo mesmo; nos demais contextos o `includeItemUuid` mantém o uuid fora
      // do payload.
      const identityField =
        includeItemUuid && item.uuid ? { uuid: item.uuid } : {}

      // Opções avançadas por item: tags (declarativo — sempre envia o array atual, [] limpa) e
      // janela de ciclo (start_cycle/total_cycles). start_cycle/total_cycles só têm efeito em
      // recorrência; nos demais contextos são ignorados pelo backend.
      const advancedItemFields = {
        tag_uuids: (item.tags || [])
          .map((tag: any) => tag.id || tag.uuid || '')
          .filter(Boolean),
        ...(item.start_cycle != null && {
          start_cycle: Number(item.start_cycle)
        }),
        ...(item.total_cycles != null && {
          total_cycles: Number(item.total_cycles)
        }),
        // Cadência do item (de quantos em quantos ciclos ele é cobrado). Herdada da
        // frequência do produto, não editável pelo merchant. O backend reaplica o mesmo
        // default, então mandar é idempotente.
        ...(item.cycle_interval != null && {
          cycle_interval: Number(item.cycle_interval)
        }),
        // Rateio por item: só faz sentido em link recorrente com cobrança proporcional,
        // mas mandar sempre é inofensivo (o backend só lê no checkout do link).
        ...(item.prorate != null && { prorate: Boolean(item.prorate) }),
        // Bonificação do item. Declarativo — mandar no_bonus limpa uma bonificação que
        // existia. O campo em R$ usa máscara money (o form guarda centavos), por isso
        // passa por priceConverter; o percentual guarda o número puro.
        bonus_policy: item.bonus_policy || 'no_bonus',
        bonus_amount:
          item.bonus_policy === 'percentage'
            ? Number(item.bonus_amount) || 0
            : priceConverter(item.bonus_amount),
        // Janela da bonificação (ciclos da recorrência, mesma regra da bonificação geral).
        // Sem bonificação volta ao default, para não deixar janela órfã no item.
        ...(!item.bonus_policy || item.bonus_policy === 'no_bonus'
          ? { bonus_start_cycle: 1, bonus_total_cycles: 0 }
          : {
              bonus_start_cycle: Math.max(
                Number(item.bonus_start_cycle) || 1,
                1
              ),
              bonus_total_cycles: Math.max(
                Number(item.bonus_total_cycles) || 0,
                0
              )
            })
      }

      // Quando quantidade é 0, enviar amount_billed zerado e amount_unit com o preço unitário
      if (quantity === 0) {
        return {
          amount_billed: 0,
          quantity: 0,
          amount_unit: formatPrice(item.price) || 0,
          ...(item.description
            ? { description: item.description }
            : item.product
            ? { product_uuid: item.product.value }
            : {}),
          ...(isLink && {
            enable_customer_select_quantity:
              item.enable_customer_select_quantity || false,
            minimum_quantity: (item as any).minimum_quantity ?? 1
          }),
          ...advancedItemFields,
          ...identityField
        }
      }

      // Caso normal: quantidade > 0
      // Arredondar para 2 casas decimais para evitar problemas de precisão de ponto flutuante
      const amountUnit = Math.round((item.total / quantity) * 100) / 100

      return {
        amount_billed: Math.round(item.total * 100) / 100,
        quantity: item.quantity,
        amount_unit: amountUnit,
        ...(item.description
          ? { description: item.description }
          : item.product
          ? { product_uuid: item.product.value }
          : {}),
        // Adicionar enable_customer_select_quantity e minimum_quantity para payment links
        ...(isLink && {
          enable_customer_select_quantity:
            item.enable_customer_select_quantity || false,
          minimum_quantity: (item as any).minimum_quantity ?? 1
        }),
        ...advancedItemFields,
        ...identityField
      }
    }),
    ...(isUnique || !isEdit
      ? { amount_billed: priceConverter(calculateTotalValue(dataForm.items)) }
      : { amount: priceConverter(calculateTotalValue(dataForm.items)) }),
    ...(dataForm.tags?.length
      ? {
          tags: dataForm.tags
            .map((tag) => tag.id || (tag as any).uuid || '')
            .filter(Boolean)
            .join(';')
        }
      : isEdit && { tags: '' })
  }

  // Montar array payments a partir do payment_type_details (novo formato)
  // O parcelamento de uma cobrança de recorrência não pode ultrapassar o número
  // de meses de um ciclo, senão a série de parcelas invade o ciclo seguinte
  // (SC-9837). O teto sai do par canônico, e não de um mapa por preset: o mapa
  // não conhecia as frequências novas (quinzenal, bimestral) nem a personalizada,
  // que caíam todas no fallback 1 e perdiam o parcelamento a que têm direito.
  // Ciclo abaixo de um mês (semanal, quinzenal) não parcela: a própria
  // recorrência já é o parcelamento.
  const isRecurrent = !isUnique
  const frequencyInstallmentCap = (() => {
    if (!isRecurrent) return Infinity
    const preset = FREQUENCY_ENUM_TO_NAME[Number(dataForm.frequency)]
    const [unit, count] = presetToPair(
      preset ?? 'monthly',
      dataForm.frequency_type,
      Number(dataForm.frequency_number) || undefined
    )
    return Math.max(1, Math.floor(monthsPerCycle(unit, count)))
  })()
  const blockInstallments = frequencyInstallmentCap <= 1
  const capInstallments = (value: number | null | undefined) =>
    Math.min(Number(value || 1), frequencyInstallmentCap)
  // SC-11827: o "sem juros" acompanha o máximo já capado pela frequência. Sem isso,
  // uma recorrência mensal (teto 1x) com "Repassar taxas do Cartão" e 3x sem juros
  // no formulário saía como max 1 / sem juros 3, e a API recusava a criação.
  const capWithoutFee = (value: number | null | undefined, max: number) =>
    Math.min(Number(value), max)
  // Quantidade fixa: min == max (o cliente não escolhe). Caso contrário min = 1
  // ("pagar em até N vezes"). Usa o mesmo max já capado pela frequência.
  const minInstallments = (
    fixed: boolean | undefined | null,
    max: number
  ): number => (fixed ? max : 1)

  // Recebimento manual (previsão) é excludente: quando ativo, é o ÚNICO meio enviado.
  // A API rejeita a combinação (400) e o model do backend também valida. Não dá para
  // sair da função aqui — o restante monta metadata, descontos, bônus etc. —, então
  // a flag apenas pula os blocos de meio de pagamento.
  const isForecastOnly =
    !!dataForm.payment_type_details?.direct_payment?.enabled
  if (isForecastOnly) {
    data.payments = [{ payment_type: 'direct_payment' }]
  }

  if (!isForecastOnly && dataForm.payment_type_details?.pix?.enabled) {
    const max_installments = capInstallments(
      dataForm.payment_type_details.pix.max_installments
    )
    data.payments?.push({
      payment_type: 'pix',
      max_installments,
      min_installments: minInstallments(
        dataForm.payment_type_details.pix.fixed_installments,
        max_installments
      )
    })
  }

  if (!isForecastOnly && dataForm.payment_type_details?.boleto?.enabled) {
    const max_installments = capInstallments(
      dataForm.payment_type_details.boleto.max_installments
    )
    data.payments?.push({
      payment_type: 'boleto',
      max_installments,
      min_installments: minInstallments(
        dataForm.payment_type_details.boleto.fixed_installments,
        max_installments
      )
    })
  }

  const cc = dataForm.payment_type_details?.credit_card
  if (!isForecastOnly && cc?.enabled) {
    const max_installments = capInstallments(cc.max_installments)
    const min_installments = minInstallments(
      cc.fixed_installments,
      max_installments
    )
    const pass_card_fees = cc.pass_card_fees

    // Verificar switches individuais
    const monthlyPayment = !!cc.credit_card?.enabled
    const recurringBilling = !!cc.credit_card_recurring?.enabled
    const installmentBilling =
      !blockInstallments && !!cc.credit_card_installments?.enabled

    // Caso especial: nenhuma opção ativa e 1 parcela => enviar credit_card padrão
    if (
      !monthlyPayment &&
      !recurringBilling &&
      !installmentBilling &&
      max_installments === 1
    ) {
      const paymentData: any = {
        payment_type: 'credit_card',
        max_installments,
        min_installments,
        pass_card_fees
      }

      if (pass_card_fees && cc.max_installments_without_fee != null) {
        paymentData.max_installments_without_fee = capWithoutFee(
          cc.max_installments_without_fee,
          max_installments
        )
      } else if (!pass_card_fees) {
        paymentData.max_installments_without_fee = max_installments
      }

      data.payments?.push(paymentData)
    } else {
      if (monthlyPayment) {
        const paymentData: any = {
          payment_type: 'credit_card',
          max_installments,
          min_installments,
          pass_card_fees
        }

        if (pass_card_fees && cc.max_installments_without_fee != null) {
          paymentData.max_installments_without_fee = capWithoutFee(
            cc.max_installments_without_fee,
            max_installments
          )
        } else if (!pass_card_fees) {
          paymentData.max_installments_without_fee = max_installments
        }

        data.payments?.push(paymentData)
      }
      if (recurringBilling) {
        const paymentData: any = {
          payment_type: 'credit_card_recurring',
          automatic_renew: !!cc.automatic_renew,
          max_installments,
          min_installments,
          pass_card_fees
        }

        if (pass_card_fees && cc.max_installments_without_fee != null) {
          paymentData.max_installments_without_fee = capWithoutFee(
            cc.max_installments_without_fee,
            max_installments
          )
        } else if (!pass_card_fees) {
          paymentData.max_installments_without_fee = max_installments
        }

        data.payments?.push(paymentData)
      }
      if (installmentBilling) {
        const paymentData: any = {
          payment_type: 'credit_card_installments',
          max_installments,
          min_installments,
          pass_card_fees
        }

        if (pass_card_fees && cc.max_installments_without_fee != null) {
          paymentData.max_installments_without_fee = capWithoutFee(
            cc.max_installments_without_fee,
            max_installments
          )
        } else if (!pass_card_fees) {
          paymentData.max_installments_without_fee = max_installments
        }

        data.payments?.push(paymentData)
      }
    }
  }

  // Handle internal identifiers
  if (
    dataForm.hasIdentifyInternal &&
    dataForm.internalIdentifiers &&
    dataForm.internalIdentifiers.length > 0
  ) {
    dataForm.internalIdentifiers.forEach(
      ({ name, value }: InternalIdentifier) => {
        data.metadata = { ...data.metadata, [name]: value }
      }
    )
  }

  // Handle discounts
  if (dataForm.discountPolicy && dataForm.discounts) {
    // Para produtos/links sempre usar days, para cobranças usar date, para recorrências usar days
    if (isCharge && !isProductOrLink) {
      // Cobranças normais: usar date e calcular days
      dataForm.discounts.forEach((discount) => {
        if (!discount.date || !discount.amount) return

        const discountDate = moment(discount.date).format('YYYY-MM-DD')

        const attributes = {
          amount: Number(discount.amount),
          days: moment(dataForm.dueDate).diff(moment(discountDate), 'days'),
          policy: discount.type === '0' ? 3 : 2
        }

        data.discounts_attributes.push(attributes)
      })
    } else {
      // Recorrências e produtos/links: usar days diretamente
      dataForm.discounts.forEach((discount) => {
        if (!discount.amount) return

        // Get days either from the days property (for recurrence/products) or calculate from date
        let days
        if (
          discount.days !== undefined &&
          discount.days !== null &&
          discount.days !== ''
        ) {
          days = Number(discount.days)
        } else if (discount.date) {
          days = moment(dataForm.dueDate).diff(moment(discount.date), 'days')
        } else {
          return // Skip if no valid days/date
        }

        // Calculate actual total_cycles (handle "Outros" option with value 13)
        // Use global form fields (discountTotalCycles, discountStartCycle) as primary source,
        // fallback to individual discount fields for backwards compatibility
        const globalTotalCycles = (dataForm as any).discountTotalCycles
        const globalTotalCyclesCustom = (dataForm as any)
          .discountTotalCyclesCustom
        const globalStartCycle = (dataForm as any).discountStartCycle

        const actualTotalCycles =
          globalTotalCycles !== undefined
            ? globalTotalCycles === 13
              ? Number(globalTotalCyclesCustom) || 0
              : globalTotalCycles
            : discount.totalCycles === 13
            ? Number(discount.totalCyclesCustom) || 0
            : discount.totalCycles ?? 0

        const actualStartCycle =
          globalStartCycle !== undefined
            ? globalStartCycle
            : discount.startCycle ?? 1

        const attributes: any = {
          amount:
            discount.type === '0'
              ? Number(discount.amount)
              : Number(
                  currency((!!discount.amount && discount.amount) || '0')
                    .replace(/\./g, '')
                    .replace(',', '.')
                ),
          days: days,
          policy: discount.type === '0' ? 3 : 2,
          total_cycles: actualTotalCycles,
          start_cycle: actualStartCycle
        }
        data.discounts_attributes.push(attributes)
      })
    }
  }

  return data
}

export const getCreditCardPaymentOptionsText = (
  isRecurrence: boolean,
  hasInstallments: boolean
): string => {
  if (!isRecurrence && !hasInstallments) {
    // Cobrança única sem possibilidade de parcelamento - não aparece
    return ''
  }

  if (!isRecurrence && hasInstallments) {
    // Cobrança única com possibilidade de parcelamento
    return 'Selecione pelo menos uma opção de pagamento para o Cartão de Crédito (Parcelado, Parcelado Recorrente ou Parcelado Manual)'
  }

  if (isRecurrence && !hasInstallments) {
    // Cobrança recorrente sem possibilidade de parcelamento
    return 'Selecione pelo menos uma opção de pagamento para o Cartão de Crédito (Cartão Recorrente ou Cartão Manual)'
  }

  if (isRecurrence && hasInstallments) {
    // Cobrança recorrente com possibilidade de parcelamento
    return 'Selecione pelo menos uma opção de pagamento para o Cartão de Crédito (Parcelado, Parcelado Recorrente ou Parcelado Manual)'
  }

  // Fallback para o texto original
  return 'Selecione pelo menos uma opção de pagamento para o Cartão de Crédito (Recorrência, À Vista ou Parcelamento)'
}
